const MAGAZINE_PAGINATION_MAX_VISIBLE_DESKTOP = 5;
const MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_COMPACT = 2;
const MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_EXPANDED = 3;

type NarrowVisibilityTier = 'compact' | 'expanded' | 'expanded-gated' | 'desktop-only';

type PaginationPageLink = {
  page: number;
  tier: NarrowVisibilityTier;
};

/**
 * Returns the inclusive [start, end] page range for numbered pagination links.
 * Uses a sliding window centred on the current page, clamped to [1, totalPages].
 */
function getVisiblePageRange(currentPage: number, totalPages: number, maxVisible = MAGAZINE_PAGINATION_MAX_VISIBLE_DESKTOP): [number, number] {
  if (totalPages <= maxVisible) {
    return [1, totalPages];
  }

  const halfWindow = Math.floor(maxVisible / 2);
  let start = Math.max(1, currentPage - halfWindow);
  let end = start + maxVisible - 1;

  if (end > totalPages) {
    end = totalPages;
    start = end - maxVisible + 1;
  }

  return [start, end];
}

function isPageInRange(page: number, [start, end]: [number, number]): boolean {
  return page >= start && page <= end;
}

function getNarrowPageVisibilityTier(page: number, compactRange: [number, number], expandedRange: [number, number], isMiddlePage: boolean): NarrowVisibilityTier {
  if (isPageInRange(page, compactRange)) {
    return 'compact';
  }

  if (isPageInRange(page, expandedRange)) {
    return isMiddlePage ? 'expanded-gated' : 'expanded';
  }

  return 'desktop-only';
}

/** Returns all page numbers in the desktop window, each with its narrow-viewport visibility tier. */
function getPaginationPageLinks(currentPage: number, totalPages: number): PaginationPageLink[] {
  if (totalPages <= 1) {
    return [];
  }

  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const isMiddlePage = hasPrevious && hasNext;

  const desktopRange = getVisiblePageRange(currentPage, totalPages, MAGAZINE_PAGINATION_MAX_VISIBLE_DESKTOP);
  const compactRange = getVisiblePageRange(currentPage, totalPages, MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_COMPACT);
  const expandedRange = getVisiblePageRange(currentPage, totalPages, MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_EXPANDED);

  const [desktopStart, desktopEnd] = desktopRange;

  return Array.from({ length: desktopEnd - desktopStart + 1 }, (_, index) => {
    const page = desktopStart + index;

    return {
      page,
      tier: getNarrowPageVisibilityTier(page, compactRange, expandedRange, isMiddlePage),
    };
  });
}

function narrowVisibilityClasses(tier: NarrowVisibilityTier): string | undefined {
  switch (tier) {
    case 'compact':
    case 'expanded':
      return undefined;
    case 'expanded-gated':
      return 'hidden xs:flex';
    case 'desktop-only':
      return 'hidden sm-md:flex';
  }
}

export {
  MAGAZINE_PAGINATION_MAX_VISIBLE_DESKTOP,
  MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_COMPACT,
  MAGAZINE_PAGINATION_MAX_VISIBLE_MOBILE_EXPANDED,
  getPaginationPageLinks,
  getVisiblePageRange,
  narrowVisibilityClasses,
  type NarrowVisibilityTier,
  type PaginationPageLink,
};
